import styled from 'styled-components';
import { pxToRem } from 'utils/convert';
import {
  caption,
  colors,
  spacing,
  fonts,
  zIndex,
  fontWeights,
  overline,
  textColors,
} from 'ui/theme';
import {
  AccordionDeprecated,
  AccordionLabelDeprecated,
  AccordionBodyDeprecated,
} from 'ui/accordion';
import { Badge, AbortedBadge } from 'ui/badge';
import { ARROW_DIRECTION_DOWN, ARROW_DIRECTION_UP } from 'ui/collapsibleContent';
import { Positioner } from 'ui/inputs/InputStyles';
import { useErrorValidationContext } from '../context/ErrorValidationContext/ErrorValidationContext';
import { useFieldErrorTheme } from './utils';

const ErrorLabel = styled.div`
  display: flex;
  font-family: ${fonts.sansSerif};
  color: ${colors.textSecondary};
  justify-content: space-between;
  width: 100%;
  align-items: center;
  white-space: nowrap;
  [data-id='styled-error-count'] {
    font-weight: ${fontWeights.semiBold};
    color: ${colors.textPrimary};
  }
`;

const ErrorBadgeContainer = styled.div`
  display: flex;
  gap: ${spacing.half};
  margin: 0 ${spacing.half};
`;

const WarningBadge = styled(Badge)`
  background: ${colors.salmon10};
  color: ${colors.warning100};
`;

const StyledAccordion = styled(AccordionDeprecated)<{
  isDesign?: boolean;
  isTemplateTakeover?: boolean;
}>`
  font-family: ${fonts.sansSerif};
  background: ${colors.white};
  border: 1px solid ${colors.grey15};
  margin-top: ${spacing.plusHalf};
  border-radius: ${spacing.threeEights};
  /** AccordionLabel */
  :first-child > button {
    padding: ${spacing.threeQuarter} ${spacing.base};
    align-items: center;
    ${({ isDesign, isTemplateTakeover }) => {
      if (isTemplateTakeover) {
        return `padding: ${spacing.half} ${spacing.threeQuarter}; min-width: ${pxToRem(
          425,
        )}; width: ${pxToRem(425)}; max-width: ${pxToRem(425)};`;
      } else if (isDesign) {
        return `padding: ${spacing.half} ${spacing.threeQuarter}; min-width: ${pxToRem(
          375,
        )}; width: ${pxToRem(375)}; max-width: ${pxToRem(375)};`;
      }
      return '';
    }}
  }
  :first-child > div {
    ${({ isDesign, isTemplateTakeover }) => {
      if (isTemplateTakeover) {
        return `min-width: ${pxToRem(425)}; width: ${pxToRem(425)}; max-width: ${pxToRem(
          425,
        )}; overflow-wrap: break-word;`;
      }
      if (isDesign) {
        return `min-width: ${pxToRem(375)}; width: ${pxToRem(375)}; max-width: ${pxToRem(
          375,
        )}; overflow-wrap: break-word;`;
      }
      return '';
    }}
  }
  [data-id='styled-accordion-body'] {
    padding: ${spacing.half} ${spacing.base} ${spacing.base};
  }
  ${({ isDesign, isTemplateTakeover }) => {
    if (isDesign || isTemplateTakeover) {
      return `position:absolute; margin-top: 0; z-index: ${zIndex.dropdown}`;
    }
    return '';
  }}
`;

const ErrorDivider = styled.div`
  margin-bottom: ${spacing.plusHalf};
  width: 100%;
`;

const AccordionWrapper = styled.div<{ isDesign?: boolean; isTemplateTakeover?: boolean }>`
  position: relative;
  ${({ isDesign, isTemplateTakeover }) => {
    if (isTemplateTakeover) {
      return `min-width: ${pxToRem(425)}; width: ${pxToRem(425)}; max-width: ${pxToRem(425)};`;
    } else if (isDesign) {
      return `min-width: ${pxToRem(375)}; width: ${pxToRem(375)}; max-width: ${pxToRem(375)};`;
    }
    return '';
  }}
`;

const StyledContainer = styled.div`
  padding-bottom: 0;
  margin: ${({ theme }) => theme.spacing.values.quarter} 0;
`;

const StyledPositioner = styled(Positioner)`
  flex-direction: row;
  column-gap: ${({ theme }) => theme.spacing.values.quarter};
`;

const StyledBannerMessage = styled.span<{ $isError?: boolean; $isWarning?: boolean }>`
  font-weight: ${fontWeights.regular};
  color: ${({ theme, $isError, $isWarning }) => {
    if ($isError) {
      return theme.colors.utilityColors.negative100;
    } else if ($isWarning) {
      return theme.colors.utilityColors.warning100;
    } else {
      return theme.colors.textColors.textPrimary;
    }
  }};
`;

const StyledSubMessage = styled.div`
  padding-left: ${({ theme }) => theme.spacing.utils.pxToRem(21)};
  ${caption};
  color: ${colors.secondaryText};
`;

const ViewErrorContainer = styled.div`
  margin: ${spacing.half} 0 ${spacing.threeQuarter} 0;
`;

export const ViewHeader = styled.div`
  ${overline};
  color: ${textColors.textSecondary};
  margin: 0 0 ${spacing.quarter} 0;
`;

export const ErrorList = styled.div`
  margin: ${spacing.eighth} 0;
`;

enum ErrorWarningType {
  ERROR = 'error',
  WARNING = 'warning',
}

type ErrorWarningTextProps = {
  isError?: boolean;
  isWarning?: boolean;
  bannerMessage: string;
  subMessage?: string;
};

type ErrorWarningBannerProps = {
  isDesign?: boolean;
};

const ErrorWarningBannerText = ({
  isError = false,
  isWarning = false,
  bannerMessage,
  subMessage,
  ...props
}: ErrorWarningTextProps) => {
  const errorTheme = useFieldErrorTheme(isError, isWarning);

  if (!isError && !isWarning) return null;
  if (!errorTheme) return null;
  const { Icon, errorColor } = errorTheme;
  return (
    <>
      {!!errorTheme && (
        <StyledContainer {...props}>
          <StyledPositioner>
            <Icon color={errorColor} />
            <StyledBannerMessage $isError={isError} $isWarning={isWarning}>
              {bannerMessage}
            </StyledBannerMessage>
          </StyledPositioner>
          {subMessage && <StyledSubMessage>{subMessage}</StyledSubMessage>}
        </StyledContainer>
      )}
    </>
  );
};

enum ErrorConsumer {
  Design = 'design',
  Detail = 'detail',
  TemplateTakeover = 'template',
}

const BaseErrorWarningAccordion = ({
  errorConsumer,
  totalErrors,
  totalWarnings,
  children,
}: {
  errorConsumer: ErrorConsumer;
  totalErrors: number;
  totalWarnings: number;
  children: React.ReactNode;
}) => {
  const totalCount = totalErrors + totalWarnings;
  const isDesign = errorConsumer === ErrorConsumer.Design;
  const isTemplateTakeover = errorConsumer === ErrorConsumer.TemplateTakeover;
  return (
    <AccordionWrapper isDesign={isDesign} isTemplateTakeover={isTemplateTakeover}>
      <StyledAccordion isDesign={isDesign} isTemplateTakeover={isTemplateTakeover}>
        <AccordionLabelDeprecated
          arrowHeight={16}
          arrowWidth={16}
          arrowStart={ARROW_DIRECTION_DOWN}
          arrowEnd={ARROW_DIRECTION_UP}
        >
          <ErrorLabel>
            <div>
              <span data-id="styled-error-count">
                {totalCount} {totalCount > 1 ? 'issues need ' : 'issue needs '}
              </span>
              your attention
            </div>
            <ErrorBadgeContainer>
              {totalErrors > 0 && (
                <AbortedBadge>
                  {totalErrors} Error{totalErrors === 1 ? '' : 's'}
                </AbortedBadge>
              )}
              {totalWarnings > 0 && (
                <WarningBadge variant="">
                  {totalWarnings} Warning{totalWarnings === 1 ? '' : 's'}
                </WarningBadge>
              )}
            </ErrorBadgeContainer>
          </ErrorLabel>
        </AccordionLabelDeprecated>
        <AccordionBodyDeprecated data-id="styled-accordion-body">
          {children}
        </AccordionBodyDeprecated>
      </StyledAccordion>
    </AccordionWrapper>
  );
};

export const ErrorWarningSummaryBanner = () => {
  const { designContentWarnings, designContentErrors, localeContentErrors, localeContentWarnings } =
    useErrorValidationContext();

  const designErrors =
    designContentErrors?.map((error) => ({
      ...error,
      type: ErrorWarningType.ERROR,
    })) || [];
  const designWarnings =
    designContentWarnings?.map((error) => ({
      ...error,
      type: ErrorWarningType.WARNING,
    })) || [];
  const contentErrors =
    localeContentErrors?.map((error) => ({
      ...error,
      type: ErrorWarningType.ERROR,
    })) || [];
  const contentWarnings =
    localeContentWarnings?.map((error) => ({
      ...error,
      type: ErrorWarningType.WARNING,
    })) || [];

  const designErrorsLength = designErrors?.length;
  const designWarningsLength = designWarnings?.length;
  const contentWarningLength = contentWarnings?.length;
  const contentErrorsLength = contentErrors?.length;

  const totalErrors = designErrorsLength + contentErrorsLength;
  const totalWarnings = designWarningsLength + contentWarningLength;

  const errorCount = totalErrors + totalWarnings;

  const errorWarningCollection: Record<string, unknown[]> = {
    Design: [...designErrors, ...designWarnings],
    Detail: [...contentErrors, ...contentWarnings],
  };
  return (
    <div>
      {errorCount > 0 && (
        <BaseErrorWarningAccordion
          errorConsumer={ErrorConsumer.TemplateTakeover}
          totalErrors={totalErrors}
          totalWarnings={totalWarnings}
        >
          {Object.keys(errorWarningCollection)?.map((key, i) => {
            const collection = errorWarningCollection[key];
            return (
              <div key={`${key}-${i}`}>
                {collection.length > 0 && (
                  <ViewErrorContainer data-test={`${key}-view-error-container`}>
                    <ViewHeader>{key}</ViewHeader>
                    <ErrorList>
                      {collection?.map((error: any, index: number) => {
                        return (
                          <ErrorWarningBannerText
                            data-test={`${key}-${error.type}-banner-${index}`}
                            key={`${error.type}-${index}`}
                            isError={error.type === ErrorWarningType.ERROR}
                            isWarning={error.type === ErrorWarningType.WARNING}
                            bannerMessage={error.errorMessage || error.__typename}
                            subMessage={error.subMessage}
                          />
                        );
                      })}
                    </ErrorList>
                  </ViewErrorContainer>
                )}
              </div>
            );
          })}
        </BaseErrorWarningAccordion>
      )}
    </div>
  );
};

export const ErrorWarningBanner = ({ isDesign = false }: ErrorWarningBannerProps) => {
  const { designContentWarnings, designContentErrors, localeContentErrors, localeContentWarnings } =
    useErrorValidationContext();

  const contentErrors = isDesign ? designContentErrors : localeContentErrors;
  const contentWarnings = isDesign ? designContentWarnings : localeContentWarnings;

  const contentWarningLength = contentWarnings.length;
  const contentErrorsLength = contentErrors.length;

  const errorCount = contentWarningLength + contentErrorsLength;
  return (
    <div>
      {errorCount > 0 && (
        <BaseErrorWarningAccordion
          errorConsumer={isDesign ? ErrorConsumer.Design : ErrorConsumer.Detail}
          totalErrors={contentErrorsLength}
          totalWarnings={contentWarningLength}
        >
          {contentErrorsLength > 0 && (
            <>
              {contentErrors?.map((error, index) => (
                <ErrorWarningBannerText
                  key={`error-${index}`}
                  isError
                  bannerMessage={error.errorMessage || error.__typename}
                  subMessage={error.subMessage}
                />
              ))}
            </>
          )}
          {contentWarningLength > 0 && contentErrorsLength > 0 && <ErrorDivider />}
          {contentWarningLength > 0 && (
            <div>
              {contentWarnings?.map((warning, index) => (
                <ErrorWarningBannerText
                  key={`warning-${index}`}
                  isWarning
                  bannerMessage={warning.errorMessage || warning.__typename}
                />
              ))}
            </div>
          )}
        </BaseErrorWarningAccordion>
      )}
    </div>
  );
};
